<template>
  <div class="pdf-doc">
    <div class="pdf-title-block">
      <div class="pdf-title-row">
        <h4 class="pdf-title" v-html="bilingual('Payment Details', 'รายละเอียดการชำระเงิน')"></h4>
        <div class="pdf-agent">{{ formatDMY() }}</div>
      </div>
      <div v-if="propertyLine" class="pdf-closing-property">{{ propertyLine }}</div>
    </div>

    <div class="pdf-closing-summary">
      <div class="pdf-closing-summary-label" v-html="bilingual('Selling Price', 'ราคาขาย')"></div>
      <div class="pdf-closing-summary-value">฿ {{ formatNumber(salePrice) }}</div>
    </div>

    <table class="pdf-closing-table">
      <thead>
        <tr>
          <th v-html="bilingual('Tax &amp; Fee Property for Calculation', 'ภาษีและค่าธรรมเนียมอสังหาริมทรัพย์สำหรับคำนวณ')"></th>
          <th class="amount" v-html="bilingual('Total', 'รวม')"></th>
          <th class="amount" v-html="bilingual('For Buyer', 'ผู้ซื้อ')"></th>
          <th class="amount" v-html="bilingual('For Seller', 'ผู้ขาย')"></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td v-html="bilingual(
            'Transfer Fee (2%)',
            'ค่าธรรมเนียมการโอน (2%)'
          )"></td>
          <td class="amount">฿ {{ formatNumber(result.transferFee) }}</td>
          <td class="amount" :class="{ 'pdf-closing-muted': !buyerAmounts.transferFee }">
            {{ buyerAmounts.transferFee ? '฿ ' + formatNumber(buyerAmounts.transferFee) : '—' }}
          </td>
          <td class="amount" :class="{ 'pdf-closing-muted': !sellerAmounts.transferFee }">
            {{ sellerAmounts.transferFee ? '฿ ' + formatNumber(sellerAmounts.transferFee) : '—' }}
          </td>
        </tr>
        <tr v-if="result.sbtOrStampDuty.type === 'sbt'">
          <td v-html="bilingual('Business Tax (3.3%)', 'ภาษีธุรกิจเฉพาะ (3.3%)')"></td>
          <td class="amount">฿ {{ formatNumber(result.sbtOrStampDuty.amount) }}</td>
          <td class="amount" :class="{ 'pdf-closing-muted': !buyerAmounts.sbtOrStampDuty }">
            {{ buyerAmounts.sbtOrStampDuty ? '฿ ' + formatNumber(buyerAmounts.sbtOrStampDuty) : '—' }}
          </td>
          <td class="amount" :class="{ 'pdf-closing-muted': !sellerAmounts.sbtOrStampDuty }">
            {{ sellerAmounts.sbtOrStampDuty ? '฿ ' + formatNumber(sellerAmounts.sbtOrStampDuty) : '—' }}
          </td>
        </tr>
        <tr v-else>
          <td v-html="bilingual(
            'Stamp Duty (0.5%)',
            'อากรแสตมป์ (0.5%)'
          )"></td>
          <td class="amount">฿ {{ formatNumber(result.sbtOrStampDuty.amount) }}</td>
          <td class="amount" :class="{ 'pdf-closing-muted': !buyerAmounts.sbtOrStampDuty }">
            {{ buyerAmounts.sbtOrStampDuty ? '฿ ' + formatNumber(buyerAmounts.sbtOrStampDuty) : '—' }}
          </td>
          <td class="amount" :class="{ 'pdf-closing-muted': !sellerAmounts.sbtOrStampDuty }">
            {{ sellerAmounts.sbtOrStampDuty ? '฿ ' + formatNumber(sellerAmounts.sbtOrStampDuty) : '—' }}
          </td>
        </tr>
        <tr>
          <td v-html="bilingual('Withholding Tax', 'ภาษีหัก ณ ที่จ่าย')"></td>
          <td class="amount">฿ {{ formatNumber(result.withholdingTax) }}</td>
          <td class="amount" :class="{ 'pdf-closing-muted': !buyerAmounts.withholdingTax }">
            {{ buyerAmounts.withholdingTax ? '฿ ' + formatNumber(buyerAmounts.withholdingTax) : '—' }}
          </td>
          <td class="amount" :class="{ 'pdf-closing-muted': !sellerAmounts.withholdingTax }">
            {{ sellerAmounts.withholdingTax ? '฿ ' + formatNumber(sellerAmounts.withholdingTax) : '—' }}
          </td>
        </tr>
        <tr class="pdf-closing-total-row">
          <td v-html="bilingual('Total Tax &amp; Fee', 'รวมภาษีและค่าธรรมเนียม')"></td>
          <td class="amount">฿ {{ formatNumber(result.total) }}</td>
          <td class="amount">
            ฿ {{ formatNumber(buyerAmounts.transferFee + buyerAmounts.sbtOrStampDuty + buyerAmounts.withholdingTax) }}
          </td>
          <td class="amount">
            ฿ {{ formatNumber(sellerAmounts.transferFee + sellerAmounts.sbtOrStampDuty + sellerAmounts.withholdingTax) }}
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pdf-closing-grand-total">
      <span v-html="bilingual('Total Property Tax Expenses', 'รวมค่าใช้จ่ายภาษีทรัพย์สิน')"></span>
      <strong>฿ {{ formatNumber(buyerTransferTotal) }}</strong>
    </div>

    <table v-if="sellerDeductions.length" class="pdf-closing-table mt-2">
      <thead>
        <tr>
          <th v-html="bilingual('Deductions', 'รายการหัก')"></th>
          <th class="amount" v-html="bilingual('Amount THB', 'จำนวนเงิน (บาท)')"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(ded, dIndex) in sellerDeductions" :key="'ded-pdf-' + dIndex">
          <td v-if="ded.label">-{{ ded.label }}</td>
          <td v-else v-html="'-' + bilingual('Deduction', 'รายการหัก')"></td>
          <td class="amount" :class="{ 'pdf-closing-negative': Number(ded.amount) < 0 }">
            {{ formatSigned(ded.amount) }}
          </td>
        </tr>
        <tr class="pdf-closing-total-row">
          <td v-html="bilingual('Total Deductions', 'รวมรายการหัก')"></td>
          <td class="amount" :class="{ 'pdf-closing-negative': sellerDeductionsTotal < 0 }">
            {{ formatSignedTotal(sellerDeductionsTotal) }}
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pdf-closing-grand-total">
      <span v-html="bilingual('Net to the Seller', 'ยอดสุทธิผู้ขาย')"></span>
      <strong>฿ {{ formatNumber(sellerNetChequeTotal) }}</strong>
    </div>

    <h5 class="pdf-section-title mt-2" v-html="bilingual('Cashiers Cheque Details', 'รายละเอียดแคชเชียร์เช็ค')"></h5>
    <table class="pdf-closing-table">
      <thead>
        <tr>
          <th>Category</th>
          <th v-html="bilingual('Beneficial', 'ผู้ได้รับผลประโยชน์')"></th>
          <th v-html="bilingual('Remark', 'หมายเหตุ')"></th>
          <th class="amount" v-html="bilingual('Amount THB', 'จำนวนเงิน (บาท)')"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="group in visibleChequeGroups">
          <tr
            v-for="(cheque, cIndex) in group.cheques"
            :key="'cheque-pdf-' + group.id + '-' + cIndex"
          >
            <td>{{ categoryLabel(group) }}</td>
            <td class="pdf-closing-name-highlight">{{ cheque.name || '—' }}</td>
            <td>{{ cheque.remark || '—' }}</td>
            <td class="amount">฿ {{ formatNumber(chequeAmount(group, cIndex, targetFor(group.kind))) }}</td>
          </tr>

          <tr v-if="modeForKind(group.kind) === 'remainder-cash'" :key="'cash-pdf-' + group.id">
            <td>{{ categoryLabel(group) }}</td>
            <td colspan="2"><span class="pdf-closing-note" v-html="bilingual('Cash', 'เงินสด')"></span></td>
            <td class="amount">
              ฿ {{ formatNumber(targetFor(group.kind) - chequeGroupTotal(group, targetFor(group.kind))) }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  formatNumber, formatDMY, ordinalSuffix, formatSigned, formatSignedTotal,
} from '../format'
import {
  modeForKind, chequeAmount, chequeGroupTotal,
} from '../chequeGroups'

export default {
  props: {
    propertyId: { type: [String, Number], default: null },
    propertyCode: { type: String, default: null },
    projectName: { type: String, default: null },
    unitNo: { type: String, default: null },
    floor: { type: String, default: null },
    tower: { type: String, default: null },
    detailProjectName: { type: String, default: null },
    detailUnitNo: { type: String, default: null },
    detailTower: { type: String, default: null },
    detailFloor: { type: String, default: null },
    salePrice: { type: Number, default: null },
    result: { type: Object, required: true },
    buyerAmounts: { type: Object, required: true },
    sellerAmounts: { type: Object, required: true },
    buyerTransferTotal: { type: Number, default: 0 },
    chequeGroups: { type: Array, required: true },
    commissionTotal: { type: Number, default: 0 },
    sellerDeductions: { type: Array, required: true },
    sellerDeductionsTotal: { type: Number, default: 0 },
    sellerNetChequeTotal: { type: Number, default: 0 },
  },
  computed: {
    propertyLine() {
      const parts = []
      if (this.propertyCode || this.propertyId) parts.push(this.propertyCode || `#${this.propertyId}`)
      if (this.detailProjectName) parts.push(this.detailProjectName)
      if (this.detailUnitNo) parts.push(`Unit No. ${this.detailUnitNo}`)
      if (this.detailFloor) parts.push(`${this.detailFloor}${ordinalSuffix(this.detailFloor)} Floor`)
      if (this.detailTower) parts.push(`Tower ${this.detailTower}`)
      return parts.join(', ')
    },
    visibleChequeGroups() {
      return this.chequeGroups.filter(group => group.cheques.length)
    },
  },
  methods: {
    formatNumber,
    formatDMY,
    ordinalSuffix,
    formatSigned,
    formatSignedTotal,
    modeForKind,
    chequeAmount,
    chequeGroupTotal,
    bilingual(en, th) {
      return `${en}<span class="pdf-th">${th}</span>`
    },
    targetFor(kind) {
      if (kind === 'government') return this.result.total
      if (kind === 'seller') return this.sellerNetChequeTotal
      if (kind === 'agent') return this.commissionTotal
      return 0
    },
    categoryLabel(group) {
      if (group.kind === 'government') return 'Government'
      if (group.kind === 'seller') return 'Seller'
      if (group.kind === 'agent') return 'Agent'
      if (group.kind === 'bank') return 'Bank Loan'
      return group.title || 'Other'
    },
  },
}
</script>
